/**
 * Product Configurator Class
 * Handles the product configuration popup functionality
 */
class ProductConfigurator {
    constructor() {
        this.configurator = null;
        this.isOpen = false;
        this.selectedVariant = null;
        this.currentStep = 1;
        this.totalSteps = 0;

        this.selectedOptions = {
            orientation: null,
            color: null,
            material: null,
            size: null,
            secondSize: null,
            upsells: []
        };

        this.init();
    }

    init() {
        this.configurator = document.querySelector('.product-configurator');

        if (!this.configurator) {
            console.warn('Product configurator element not found');
            return;
        }

        this.setupStepIndices();

        this.bindEvents();
        this.setupSteps();
        this.checkColorAvailability();

        if (this.isVariantsAsSeparateProductsMode()) {
            this.autoSelectFirstColor();
        }

        // Auto-select active orientation if available
        this.autoSelectActiveOrientation();
    }

    setupStepIndices() {
        const steps = this.configurator.querySelectorAll('.product-configurator__wrapper');

        steps.forEach((step, index) => {
            step.dataset.step = (index + 1).toString();
        });

        this.totalSteps = steps.length;
    }

    setupSteps() {
        const steps = this.configurator.querySelectorAll('.product-configurator__wrapper');

        steps.forEach(step => {
            step.classList.remove('active');
        });

        if (steps.length > 0) {
            const firstStep = steps[0];
            firstStep.classList.add('active');
            this.currentStep = parseInt(firstStep.dataset.step);
        }

        this.updateProgressBar();
    }

    bindEvents() {
        document.addEventListener('click', (e) => {
            if (e.target.closest('[data-popup-configurator-open]')) {
                e.preventDefault();
                this.openPopup();
            }
        });

        this.configurator.addEventListener('click', (e) => {
            if (e.target.closest('.product-configurator__close') ||
                e.target.classList.contains('product-configurator')) {
                e.preventDefault();
                this.closePopup();
            }
        });

        this.configurator.addEventListener('click', (e) => {
            if (e.target.closest('[data-popup-configurator-cart]')) {
                e.preventDefault();
                this.addToCart();
            }
        });

        this.configurator.addEventListener('click', (e) => {
            const orientationItem = e.target.closest('.product-configurator__options--item');
            if (orientationItem) {
                e.preventDefault();
                this.selectOrientation(orientationItem);
            }
        });

        this.configurator.addEventListener('click', (e) => {
            const colorItem = e.target.closest('.product-configurator__colors--item');
            if (colorItem && !colorItem.classList.contains('sold-out')) {
                e.preventDefault();
                this.selectColor(colorItem);
            }
        });

        this.configurator.addEventListener('click', (e) => {
            const sizeItem = e.target.closest('.product-configurator__sizes--item');
            if (sizeItem) {
                e.preventDefault();
                this.selectSize(sizeItem);
            }
        });

        this.configurator.addEventListener('click', (e) => {
            const upsellBtn = e.target.closest('.product-configurator__upsells--btn');
            if (upsellBtn) {
                e.preventDefault();
                this.selectUpsell(upsellBtn);
            }
        });

        this.configurator.addEventListener('click', (e) => {
            const quantityBtn = e.target.closest('.quantity-btn');
            if (quantityBtn) {
                e.preventDefault();
                this.handleQuantityChange(quantityBtn);
            }
        });

        // Add quantity selector handlers
        this.configurator.addEventListener('click', (e) => {
            const quantitySelectorBtn = e.target.closest('.product-configurator__quantity .quantity-btn');
            if (quantitySelectorBtn) {
                e.preventDefault();
                this.handleQuantitySelectorChange(quantitySelectorBtn);
            }
        });

        this.configurator.addEventListener('click', (e) => {
            if (e.target.closest('.product-configurator__next')) {
                e.preventDefault();
                this.nextStep();
            }
        });

        this.configurator.addEventListener('click', (e) => {
            if (e.target.closest('.product-configurator__prev')) {
                e.preventDefault();
                this.prevStep();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closePopup();
            }
        });
    }

    openPopup() {
        this.configurator.classList.add('active');

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        this.isOpen = true;

        const firstStep = this.configurator.querySelector('.product-configurator__wrapper');
        if (firstStep) {
            this.currentStep = parseInt(firstStep.dataset.step);
            this.updateStepVisibility();
        }

        if (this.isVariantsAsSeparateProductsMode() && (!this.selectedOptions.color || !this.selectedOptions.material)) {
            this.autoSelectFirstColor();
        }

        // Auto-select orientation when opening popup
        if (!this.selectedOptions.orientation) {
            this.autoSelectActiveOrientation();
        }

        this.checkColorAvailability();
    }

    closePopup() {
        this.configurator.classList.remove('active');

        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';

        this.isOpen = false;
    }

    goToStep(stepNumber) {
        if (stepNumber < 1 || stepNumber > this.totalSteps) return;

        this.currentStep = stepNumber;

        const steps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        steps.forEach(step => {
            step.classList.remove('active');
        });

        const currentStepElement = this.configurator.querySelector(`[data-step="${stepNumber}"]`);
        if (currentStepElement) {
            currentStepElement.classList.add('active');
        }

        this.updateProgressBar();
        this.checkColorAvailability();
    }

    nextStep() {
        // Save active size before moving to next step
        this.saveActiveSizeForCurrentStep();

        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        const stepArray = Array.from(allSteps);
        const currentStepIndex = stepArray.findIndex(step => parseInt(step.dataset.step) === this.currentStep);

        if (currentStepIndex < stepArray.length - 1) {
            const nextStep = stepArray[currentStepIndex + 1];
            this.currentStep = parseInt(nextStep.dataset.step);
            this.updateStepVisibility();
            this.updateProgressBar();

            this.hideErrorInConfigurator();
        }

        if (this.isVariantsAsSeparateProductsMode() && (!this.selectedOptions.color || !this.selectedOptions.material)) {
            this.autoSelectFirstColor();
        }

        // Check if current step is upsells step
        const currentStepElement = this.configurator.querySelector(`[data-step="${this.currentStep}"]`);
        if (currentStepElement && currentStepElement.hasAttribute('data-upsells-step')) {
            this.filterUpsellsBySelectedOptions();
        }

        if (this.isVariantsAsSeparateProductsMode()) {
            this.autoSelectFirstSizeForCurrentStep();

            if (this.selectedOptions.additionalProduct1 && this.selectedOptions.additionalProduct1VariantId) {
                this.addAdditionalProductToUpsells(
                    this.selectedOptions.additionalProduct1VariantId,
                    `Additional Product 1 (${this.selectedOptions.additionalProduct1.value})`,
                    1,
                    this.selectedOptions.additionalProduct1.value
                );
            }

            if (this.selectedOptions.additionalProduct2 && this.selectedOptions.additionalProduct2VariantId) {
                this.addAdditionalProductToUpsells(
                    this.selectedOptions.additionalProduct2VariantId,
                    `Additional Product 2 (${this.selectedOptions.additionalProduct2.value})`,
                    2,
                    this.selectedOptions.additionalProduct2.value
                );
            }

            if (this.selectedOptions.additionalProduct3 && this.selectedOptions.additionalProduct3VariantId) {
                this.addAdditionalProductToUpsells(
                    this.selectedOptions.additionalProduct3VariantId,
                    `Additional Product 3 (${this.selectedOptions.additionalProduct3.value})`,
                    3,
                    this.selectedOptions.additionalProduct3.value
                );
            }
        }
    }

    // Add new function to save active size
    saveActiveSizeForCurrentStep() {
        // Check that current step contains sizes (steps 3-7, except upsells)
        if (this.currentStep >= 3 && this.currentStep <= 7) {
            const currentStepElement = this.configurator.querySelector(`[data-step="${this.currentStep}"]`);
            if (!currentStepElement) {
                return;
            }

            // Check that this is not an upsells step
            if (currentStepElement.hasAttribute('data-upsells-step')) {
                return;
            }

            // Find active size in current step
            const activeSizeItem = currentStepElement.querySelector('.product-configurator__sizes--item.active');
            if (activeSizeItem) {
                // If there is an active size but it's not saved in selectedOptions, save it
                const sizeValue = activeSizeItem.dataset.size;
                const sizeCm = activeSizeItem.dataset.sizeCm;

                if (this.isVariantsAsSeparateProductsMode()) {
                    // For separate products mode
                    if (this.currentStep === 3) {
                        if (!this.selectedOptions.size || this.selectedOptions.size.value !== sizeValue) {
                            this.selectedOptions.size = {
                                value: sizeValue,
                                cm: sizeCm
                            };
                        }
                    } else if (this.currentStep === 4) {
                        if (!this.selectedOptions.additionalProduct1 || this.selectedOptions.additionalProduct1.value !== sizeValue) {
                            this.selectedOptions.additionalProduct1 = {
                                value: sizeValue,
                                cm: sizeCm
                            };
                        }
                    } else if (this.currentStep === 5) {
                        if (!this.selectedOptions.additionalProduct2 || this.selectedOptions.additionalProduct2.value !== sizeValue) {
                            this.selectedOptions.additionalProduct2 = {
                                value: sizeValue,
                                cm: sizeCm
                            };
                        }
                    } else if (this.currentStep === 6) {
                        if (!this.selectedOptions.additionalProduct3 || this.selectedOptions.additionalProduct3.value !== sizeValue) {
                            this.selectedOptions.additionalProduct3 = {
                                value: sizeValue,
                                cm: sizeCm
                            };
                        }
                    }
                } else {
                    // For normal mode
                    if (this.currentStep === 3) {
                        if (!this.selectedOptions.size || this.selectedOptions.size.value !== sizeValue) {
                            this.selectedOptions.size = {
                                value: sizeValue,
                                cm: sizeCm
                            };
                        }
                    } else if (this.currentStep === 4) {
                        if (!this.selectedOptions.secondSize || this.selectedOptions.secondSize.value !== sizeValue) {
                            this.selectedOptions.secondSize = {
                                value: sizeValue,
                                cm: sizeCm
                            };
                        }
                    }
                }
            }
        }
    }

    autoSelectFirstSizeForCurrentStep() {
        // Update range to support step 7
        if (this.currentStep >= 3 && this.currentStep <= 7) {
            const currentStepElement = this.configurator.querySelector(`[data-step="${this.currentStep}"]`);
            if (!currentStepElement) {
                return;
            }

            // Check that this is not an upsells step
            if (currentStepElement.hasAttribute('data-upsells-step')) {
                return;
            }

            const activeSizeItem = currentStepElement.querySelector('.product-configurator__sizes--item.active');
            if (activeSizeItem) {
                return;
            }

            const firstSizeItem = currentStepElement.querySelector('.product-configurator__sizes--item');
            if (firstSizeItem) {
                this.selectSize(firstSizeItem);
            }
        }
    }

    prevStep() {
        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        const stepArray = Array.from(allSteps);
        const currentStepIndex = stepArray.findIndex(step => parseInt(step.dataset.step) === this.currentStep);

        if (currentStepIndex > 0) {
            const prevStep = stepArray[currentStepIndex - 1];
            this.currentStep = parseInt(prevStep.dataset.step);
            this.updateStepVisibility();
            this.updateProgressBar();

            this.hideErrorInConfigurator();
        }
    }

    updateProgressBar() {
        const currentStepElement = this.configurator.querySelector(`[data-step="${this.currentStep}"]`);
        if (!currentStepElement) return;

        const progressBar = currentStepElement.querySelector('.product-configurator__progress');
        if (!progressBar) return;

        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        const stepArray = Array.from(allSteps);
        const currentStepIndex = stepArray.findIndex(step => parseInt(step.dataset.step) === this.currentStep);

        if (currentStepIndex === -1) return;

        const progressSteps = progressBar.querySelectorAll('[data-progress-bar-step]');

        progressSteps.forEach(step => {
            step.classList.remove('active', 'visible');
        });

        // Show all steps up to current
        for (let i = 0; i < stepArray.length && i < progressSteps.length; i++) {
            progressSteps[i].classList.add('visible');
        }

        // Activate current step
        if (currentStepIndex < progressSteps.length) {
            progressSteps[currentStepIndex].classList.add('active');
        }
    }

    updateStepVisibility() {
        const steps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        steps.forEach(step => {
            step.classList.remove('active');
        });

        const currentStepElement = this.configurator.querySelector(`[data-step="${this.currentStep}"]`);
        if (currentStepElement) {
            currentStepElement.classList.add('active');
        }

        this.updateProgressBar();
        this.checkColorAvailability();
    }

    // New function to auto-select active orientation
    autoSelectActiveOrientation() {
        const orientationStep = this.configurator.querySelector('[data-orientation-step="true"]');
        if (!orientationStep) {
            console.log('No orientation step found');
            return;
        }

        const activeOrientationItem = orientationStep.querySelector('.product-configurator__options--item.active');
        if (activeOrientationItem) {
            console.log('Found active orientation, auto-selecting it');
            this.selectOrientation(activeOrientationItem);
        } else {
            console.log('No active orientation found');
        }
    }

    // Updated selectOrientation function with better logging
    selectOrientation(orientationItem) {
        const orientationItems = this.configurator.querySelectorAll('.product-configurator__options--item');
        orientationItems.forEach(item => {
            item.classList.remove('active');
        });

        orientationItem.classList.add('active');

        const orientationIndex = orientationItem.dataset.orientation;
        const orientationTitle = orientationItem.querySelector('h3')?.textContent || 'Unknown';
        const orientationIcon = orientationItem.querySelector('img')?.src || '';

        this.selectedOptions.orientation = {
            index: orientationIndex,
            title: orientationTitle,
            icon: orientationIcon
        };

        console.log(`Orientation selected: ${orientationTitle} (${orientationIndex})`);

        // Update any existing cart items with new orientation
        this.updateCartItemProperties();
    }

    // New function to update cart item properties when orientation changes
    updateCartItemProperties() {
        // This function can be used to update properties of items already in cart
        // For now, it's a placeholder for future functionality
        console.log('Orientation changed, cart item properties updated');
    }

    selectColor(colorItem) {
        const allColorItems = this.configurator.querySelectorAll('.product-configurator__colors--item');
        allColorItems.forEach(item => {
            item.classList.remove('active');
        });

        colorItem.classList.add('active');

        const variantId = colorItem.dataset.variantId;
        const variantUrl = colorItem.dataset.variantUrl;
        const variantImage = colorItem.dataset.variantImage;
        const configuratorImage = colorItem.dataset.configuratorImage || variantImage;
        const variantPrice = colorItem.dataset.variantPrice;
        const material = colorItem.dataset.material;
        const color = colorItem.dataset.color;

        console.log(`selectColor called with color: ${color}, material: ${material}`);

        this.updateConfiguratorImages(configuratorImage);
        // this.updateMainProductImages(variantImage);

        this.selectedVariant = {
            id: variantId,
            url: variantUrl,
            image: variantImage,
            price: variantPrice
        };

        this.selectedOptions.color = color;
        this.selectedOptions.material = material;

        console.log(`selectedOptions updated: color=${this.selectedOptions.color}, material=${this.selectedOptions.material}`);

        this.hideErrorInConfigurator();
        this.updateProductUrl(variantId);
        this.filterUpsellsBySelectedOptions();
    }

    selectSize(sizeItem) {
        const currentStep = sizeItem.closest('.product-configurator__wrapper');
        const stepNumber = currentStep.dataset.step;

        const sizeItemsInStep = currentStep.querySelectorAll('.product-configurator__sizes--item');
        sizeItemsInStep.forEach(item => item.classList.remove('active'));
        sizeItem.classList.add('active');

        const sizeValue = sizeItem.dataset.size;
        const sizeCm = sizeItem.dataset.sizeCm;

        console.log(`selectSize called for step ${stepNumber} with size ${sizeValue}`);

        if (this.isVariantsAsSeparateProductsMode()) {
            this.handleSeparateProductsSizeSelection(stepNumber, sizeValue, sizeCm);
        } else {
            // For regular products, check if this is a single-step size product
            const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
            const totalSteps = allSteps.length;

            console.log(`Total steps: ${totalSteps}, current step: ${stepNumber}`);

            // Check if current step has data-sizes-step="true" attribute
            const isSizesStep = currentStep.hasAttribute('data-sizes-step');
            console.log(`Is sizes step: ${isSizesStep}`);

            if (isSizesStep) {
                // This is a sizes step - save the size
                this.selectedOptions.size = {
                    value: sizeValue,
                    cm: sizeCm
                };
                console.log(`Saved size: ${sizeValue} to selectedOptions.size`);
            } else if (totalSteps === 4 && stepNumber === '4') {
                // Two step size product - second size
                this.selectedOptions.secondSize = {
                    value: sizeValue,
                    cm: sizeCm
                };
                console.log(`Saved second size: ${sizeValue} to selectedOptions.secondSize`);
            }

            this.hideErrorInConfigurator();
            this.findAndApplyMatchingVariant();
        }
    }

    isVariantsAsSeparateProductsMode() {
        return this.configurator.dataset.variantsAsSeparateProducts === 'true';
    }

    handleSeparateProductsSizeSelection(stepNumber, sizeValue, sizeCm) {
        console.log(`handleSeparateProductsSizeSelection called for step ${stepNumber} with size ${sizeValue}`);

        // Get total number of steps
        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        const totalSteps = allSteps.length;

        console.log(`Total steps: ${totalSteps}, current step: ${stepNumber}`);

        if (stepNumber === '3') {
            this.selectedOptions.size = {
                value: sizeValue,
                cm: sizeCm
            };

            this.findAndApplyMainProductVariant(sizeValue);
        } else if (stepNumber === '2') {
            // Check if additionalProduct1 is already set
            if (!this.selectedOptions.additionalProduct1) {
                this.selectedOptions.additionalProduct1 = {
                    value: sizeValue,
                    cm: sizeCm
                };
                this.findAndAddAdditionalProductToUpsells(1, sizeValue);
            } else {
                console.log(`Additional product 1 already set to ${this.selectedOptions.additionalProduct1.value}, ignoring ${sizeValue}`);
            }
        } else if (stepNumber === '4') {
            // Check if additionalProduct2 is already set
            if (!this.selectedOptions.additionalProduct2) {
                this.selectedOptions.additionalProduct2 = {
                    value: sizeValue,
                    cm: sizeCm
                };
                this.findAndAddAdditionalProductToUpsells(2, sizeValue);
            } else {
                console.log(`Additional product 2 already set to ${this.selectedOptions.additionalProduct2.value}, ignoring ${sizeValue}`);
            }
        } else if (stepNumber === '5') {
            // Check if additionalProduct3 is already set
            if (!this.selectedOptions.additionalProduct3) {
                this.selectedOptions.additionalProduct3 = {
                    value: sizeValue,
                    cm: sizeCm
                };
                this.findAndAddAdditionalProductToUpsells(3, sizeValue);
            } else {
                console.log(`Additional product 3 already set to ${this.selectedOptions.additionalProduct3.value}, ignoring ${sizeValue}`);
            }
        }
    }

    findAndAddAdditionalProductToUpsells(productNumber, sizeValue) {
        console.log(`findAndAddAdditionalProductToUpsells called for product ${productNumber} with size ${sizeValue}`);

        if (!this.selectedOptions.color || !this.selectedOptions.material) {
            console.log(`Missing color or material for product ${productNumber}`);
            // Don't show error here - just return
            return;
        }

        const variants = window.productVariants || [];
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;

        console.log(`Looking for product ${productNumber} with size: ${sizeValue}, material: ${selectedMaterial}, color: ${selectedColor}`);
        console.log(`Available variants:`, variants.length);

        for (let variant of variants) {
            const title = variant.title.toLowerCase();
            const sizeMatch = title.includes(sizeValue.toLowerCase());
            const materialMatch = title.includes(selectedMaterial.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = selectedColor.replace(/-/g, ' ');
            const colorWithDashes = selectedColor.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            console.log(`Variant ${variant.id}: "${variant.title}" - sizeMatch: ${sizeMatch}, materialMatch: ${materialMatch}, colorMatch: ${colorMatch}`);

            if (sizeMatch && materialMatch && colorMatch) {
                console.log(`Found matching variant for product ${productNumber}: ${variant.id}`);
                this.addAdditionalProductToUpsells(variant.id, variant.title, productNumber, sizeValue);
                return;
            }
        }

        console.log(`No matching variant found for product ${productNumber}`);
        // Don't show error here - just log and continue
    }

    addAdditionalProductToUpsells(variantId, variantTitle, productNumber, sizeValue) {
        if (!this.selectedOptions.upsells) {
            this.selectedOptions.upsells = [];
        }

        const additionalProductData = {
            id: variantId,
            title: `${variantTitle} (${sizeValue})`,
            price: this.getVariantPrice(variantId),
            quantity: 1,
            isAdditionalProduct: true,
            productNumber: productNumber,
            sizeValue: sizeValue,
            properties: this.buildProductProperties() // Add properties to upsells
        };

        const existingIndex = this.selectedOptions.upsells.findIndex(item =>
            item.isAdditionalProduct && item.productNumber === productNumber
        );

        if (existingIndex === -1) {
            this.selectedOptions.upsells.push(additionalProductData);
        } else {
            this.selectedOptions.upsells[existingIndex] = additionalProductData;
        }
    }

    getVariantPrice(variantId) {
        const variants = window.productVariants || [];
        const variant = variants.find(v => v.id === variantId);

        if (variant && variant.price) {
            return variant.price;
        }

        const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);
        if (variantElement && variantElement.dataset.variantPrice) {
            return variantElement.dataset.variantPrice;
        }

        return '€0.00';
    }

    findAndApplyMainProductVariant(sizeValue) {
        if (!this.selectedOptions.color || !this.selectedOptions.material) {
            // Don't show error here - just return
            return;
        }

        const variants = window.productVariants || [];
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;

        for (let variant of variants) {
            const title = variant.title.toLowerCase();
            const sizeMatch = title.includes(sizeValue.toLowerCase());
            const materialMatch = title.includes(selectedMaterial.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = selectedColor.replace(/-/g, ' ');
            const colorWithDashes = selectedColor.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            if (sizeMatch && materialMatch && colorMatch) {
                this.updateProductWithVariant(variant.id);
                return;
            }
        }

        // Don't show error here - just return
    }

    findAndApplyAdditionalProductVariant(productNumber, sizeValue) {
        if (!this.selectedOptions.color || !this.selectedOptions.material) {
            // Don't show error here - just return
            return;
        }

        const variants = window.productVariants || [];
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;

        for (let variant of variants) {
            const title = variant.title.toLowerCase();
            const sizeMatch = title.includes(sizeValue.toLowerCase());
            const materialMatch = title.includes(selectedMaterial.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = selectedColor.replace(/-/g, ' ');
            const colorWithDashes = selectedColor.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            if (sizeMatch && materialMatch && colorMatch) {
                if (productNumber === 1) {
                    this.selectedOptions.additionalProduct1VariantId = variant.id;
                } else if (productNumber === 2) {
                    this.selectedOptions.additionalProduct2VariantId = variant.id;
                } else if (productNumber === 3) {
                    this.selectedOptions.additionalProduct3VariantId = variant.id;
                }

                return;
            }
        }

        // Don't show error here - just return
    }

    findAndApplyMatchingVariant() {
        const exactVariantId = this.findExactVariantMatch();

        if (exactVariantId) {
            this.updateProductWithVariant(exactVariantId);
            return;
        }

        if (this.selectedOptions.color && this.selectedOptions.material) {
            const activeColorElement = this.configurator.querySelector('.product-configurator__colors--item.active');

            if (activeColorElement) {
                const variantId = activeColorElement.dataset.variantId;
                this.updateProductWithVariant(variantId);
            }
        }

        // Only show error if we're in addToCart context
        // For now, don't show error here as it might be called during normal selection
    }

    findExactVariantMatch() {
        const variants = window.productVariants || [];
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;
        const selectedSize = this.selectedOptions.size?.value;
        const selectedSecondSize = this.selectedOptions.secondSize?.value;

        let targetSizeCombination = '';
        if (selectedSize && selectedSecondSize) {
            if (selectedSize !== selectedSecondSize) {
                // Normalize size combinations - sort alphabetically
                const sizes = [selectedSize, selectedSecondSize].sort();
                targetSizeCombination = `${sizes[0]} + ${sizes[1]}`;
            } else {
                targetSizeCombination = `${selectedSize} + ${selectedSecondSize}`;
            }
        } else if (selectedSize) {
            targetSizeCombination = selectedSize;
        } else if (selectedSecondSize) {
            targetSizeCombination = selectedSecondSize;
        }

        const sizeMatches = [];

        for (let variant of variants) {
            const title = variant.title.toLowerCase();

            let sizeMatch = false;

            if (targetSizeCombination) {
                const targetSizeLower = targetSizeCombination.toLowerCase();

                if (selectedSize && selectedSecondSize && selectedSize === selectedSecondSize) {
                    const exactCombination = `${selectedSize} + ${selectedSecondSize}`;
                    sizeMatch = title.includes(exactCombination.toLowerCase());
                } else if (selectedSize && selectedSecondSize) {
                    // For combination of two different sizes, look for both sizes in the title
                    const size1 = selectedSize.toLowerCase();
                    const size2 = selectedSecondSize.toLowerCase();
                    sizeMatch = title.includes(size1) && title.includes(size2);
                } else if (selectedSize && !selectedSecondSize) {
                    // If only first size is selected, look for variants containing this size
                    sizeMatch = title.includes(targetSizeLower);
                } else {
                    sizeMatch = title.includes(targetSizeLower);
                }
            } else {
                sizeMatch = true;
            }

            if (sizeMatch) {
                sizeMatches.push(variant);
            }
        }

        // Check all variants for material and color match
        for (let variant of sizeMatches) {
            const title = variant.title.toLowerCase();

            let materialMatch = true;
            if (selectedMaterial) {
                const selectedMaterialLower = selectedMaterial.toLowerCase();
                materialMatch = title.includes(selectedMaterialLower);
            }

            let colorMatch = true;
            if (selectedColor) {
                const selectedColorLower = selectedColor.toLowerCase();
                const selectedColorNormalized = selectedColorLower.replace(/-/g, ' ');
                colorMatch = title.includes(selectedColorLower) || title.includes(selectedColorNormalized);
            }

            if (materialMatch && colorMatch) {
                return variant.id;
            }
        }

        // Don't show error here - just return null
        return null;
    }

    showErrorInConfigurator() {
        const errorElement = this.configurator.querySelector('.product-configurator__error');
        if (!errorElement) {
            console.error('Error element not found');
            return;
        }

        errorElement.style.display = 'flex';

        const closeBtn = errorElement.querySelector('.product-configurator__error--btn');
        if (closeBtn) {
            const newCloseBtn = closeBtn.cloneNode(true);
            closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

            newCloseBtn.addEventListener('click', () => {
                this.hideErrorInConfigurator();
            });
        }
    }

    hideErrorInConfigurator() {
        const errorElement = this.configurator.querySelector('.product-configurator__error');
        if (errorElement) {
            errorElement.style.display = 'none';
        }
    }

    updateProductWithVariant(variantId) {
        if (!variantId) {
            return;
        }

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', currentUrl.toString());

        const variantInputs = document.querySelectorAll('input[name="id"]');
        variantInputs.forEach(input => {
            input.value = variantId;
        });

        const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);

        if (variantElement) {
            const variantImage = variantElement.dataset.variantImage;
            const configuratorImage = variantElement.dataset.configuratorImage || variantImage;
            this.updateConfiguratorImages(configuratorImage);
            // this.updateMainProductImages(variantImage);
        }
    }

    updateConfiguratorImages(variantImage) {
        if (!variantImage) return;

        const allProductImages = this.configurator.querySelectorAll('.product-configurator__media--image img');

        allProductImages.forEach(img => {
            const mediaContainer = img.closest('.product-configurator__media--image');
            const isSizesStep = mediaContainer?.classList.contains('product-configurator__media--image-sizes');

            if (!isSizesStep) {
                img.src = variantImage;
                if (img.srcset) {
                    img.srcset = variantImage + ' 1x, ' + variantImage + ' 2x';
                }
            }
        });

        const allMediaElements = this.configurator.querySelectorAll('.product-configurator__media--image');
        allMediaElements.forEach(element => {
            const isSizesStep = element.classList.contains('product-configurator__media--image-sizes');

            if (!isSizesStep && element.style.backgroundImage) {
                element.style.backgroundImage = `url('${variantImage}')`;
            }
        });
    }

    // updateMainProductImages(variantImage) {
    //   if (!variantImage) return;

    //   // Only update the first/active image to preserve the gallery functionality
    //   const activeImage = document.querySelector('.product__slides .media--active img, .product-single__photos .media--active img');

    //   if (activeImage) {
    //     activeImage.src = variantImage;

    //     if (activeImage.srcset) {
    //       activeImage.srcset = variantImage + ' 1x, ' + variantImage + ' 2x';
    //     }

    //     if (activeImage.dataset.src) {
    //       activeImage.dataset.src = variantImage;
    //     }
    //     if (activeImage.dataset.srcset) {
    //       activeImage.dataset.srcset = variantImage + ' 1x, ' + variantImage + ' 2x';
    //     }
    //   } else {
    //     // Fallback: if no active image found, update the first image only
    //     const firstImage = document.querySelector('.product__slides img, .product-single__photos img');
    //     if (firstImage) {
    //       firstImage.src = variantImage;

    //       if (firstImage.srcset) {
    //         firstImage.srcset = variantImage + ' 1x, ' + variantImage + ' 2x';
    //       }

    //       if (firstImage.dataset.src) {
    //         firstImage.dataset.src = variantImage;
    //       }
    //       if (firstImage.dataset.srcset) {
    //         firstImage.dataset.srcset = variantImage + ' 1x, ' + variantImage + ' 2x';
    //       }
    //     }
    //   }

    //   const imageUpdateEvent = new CustomEvent('productImageUpdated', {
    //     detail: { image: variantImage }
    //   });
    //   document.dispatchEvent(imageUpdateEvent);
    // }

    updateProductUrl(variantId) {
        if (!variantId) return;

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', currentUrl.toString());

        this.updateProductLinks(variantId);
    }

    updateProductLinks(variantId) {
        const productLinks = document.querySelectorAll('a[href*="/products/"], form[action*="/cart/add"]');

        productLinks.forEach(link => {
            const url = new URL(link.href || link.action, window.location.origin);

            if (url.pathname.includes('/products/') && url.pathname.includes(window.location.pathname.split('/products/')[1]?.split('?')[0])) {
                url.searchParams.set('variant', variantId);

                if (link.href) {
                    link.href = url.toString();
                } else if (link.action) {
                    link.action = url.toString();
                }
            }
        });

        const variantInputs = document.querySelectorAll('input[name="id"], input[name="variant_id"]');
        variantInputs.forEach(input => {
            input.value = variantId;
        });
    }

    selectUpsell(upsellBtn) {
        const upsellItem = upsellBtn.closest('.product-configurator__upsells--item');
        const productId = upsellBtn.dataset.productId;
        const productTitle = upsellBtn.dataset.productTitle;
        const productPrice = upsellBtn.dataset.productPrice;

        upsellBtn.style.display = 'none';
        const quantityPicker = upsellItem.querySelector('.product-configurator__upsells--quantity');
        quantityPicker.style.display = 'flex';

        this.addUpsellToSelection(upsellItem, productId, productTitle, productPrice, 1);
    }

    addUpsellToSelection(upsellItem, productId, productTitle, productPrice, quantity) {
        if (!this.selectedOptions.upsells) {
            this.selectedOptions.upsells = [];
        }

        const upsellData = {
            id: productId,
            title: productTitle,
            price: productPrice,
            quantity: quantity,
            properties: this.buildProductProperties() // Add properties to upsells
        };

        const existingIndex = this.selectedOptions.upsells.findIndex(item => item.id === productId);
        if (existingIndex === -1) {
            this.selectedOptions.upsells.push(upsellData);
        } else {
            this.selectedOptions.upsells[existingIndex].quantity = quantity;
            this.selectedOptions.upsells[existingIndex].properties = upsellData.properties;
        }
    }

    handleQuantityChange(quantityBtn) {
        const upsellItem = quantityBtn.closest('.product-configurator__upsells--item');
        const action = quantityBtn.dataset.action;
        const quantitySpan = upsellItem.querySelector('.quantity-number');
        let currentQuantity = parseInt(quantitySpan.textContent);

        if (action === 'increase') {
            currentQuantity++;
            quantitySpan.textContent = currentQuantity;

            const productId = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productId;
            const productTitle = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productTitle;
            const productPrice = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productPrice;

            this.addUpsellToSelection(upsellItem, productId, productTitle, productPrice, currentQuantity);

        } else if (action === 'decrease') {
            currentQuantity--;

            if (currentQuantity <= 0) {
                this.removeUpsellFromSelection(upsellItem);

                const quantityPicker = upsellItem.querySelector('.product-configurator__upsells--quantity');
                const addButton = upsellItem.querySelector('.product-configurator__upsells--btn');

                quantityPicker.style.display = 'none';
                addButton.style.display = 'block';

            } else {
                quantitySpan.textContent = currentQuantity;

                const productId = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productId;
                const productTitle = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productTitle;
                const productPrice = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productPrice;

                this.addUpsellToSelection(upsellItem, productId, productTitle, productPrice, currentQuantity);
            }
        }
    }

    removeUpsellFromSelection(upsellItem) {
        if (!this.selectedOptions.upsells) return;

        const productId = upsellItem.querySelector('.product-configurator__upsells--btn').dataset.productId;
        this.selectedOptions.upsells = this.selectedOptions.upsells.filter(item => item.id !== productId);
    }

    getSelectedOptionsNote() {
        const notes = [];

        if (this.selectedOptions.orientation) {
            notes.push(`Orientation: ${this.selectedOptions.orientation.title}`);
        }

        return notes.join(', ');
    }

    getConfigurationData() {
        return {
            variant: this.selectedVariant,
            options: this.selectedOptions,
            note: this.getSelectedOptionsNote()
        };
    }

    applyVariant() {
        if (!this.selectedVariant) {
            console.warn('No variant selected');
            return;
        }

        if (this.selectedVariant.url) {
            window.location.href = this.selectedVariant.url;
            return;
        }

        if (this.selectedVariant.id) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('variant', this.selectedVariant.id);
            window.location.href = currentUrl.toString();
            return;
        }
    }

    // Add new function to handle quantity selector changes
    handleQuantitySelectorChange(quantityBtn) {
        const quantityContainer = quantityBtn.closest('.product-configurator__quantity');
        if (!quantityContainer) return;

        const quantityInput = quantityContainer.querySelector('.quantity-input');
        if (!quantityInput) return;

        let currentQuantity = parseInt(quantityInput.value) || 1;

        // Determine action by button content or position
        const isIncrease = quantityBtn.textContent.includes('+') ||
            quantityBtn.querySelector('svg')?.innerHTML.includes('M8 15.5V0.5');
        const isDecrease = quantityBtn.textContent.includes('-') ||
            quantityBtn.querySelector('svg')?.innerHTML.includes('M15.5 8H0.5') &&
            !quantityBtn.querySelector('svg')?.innerHTML.includes('M8 15.5V0.5');

        if (isIncrease) {
            currentQuantity++;
            quantityInput.value = currentQuantity;
        } else if (isDecrease && currentQuantity > 1) {
            currentQuantity--;
            quantityInput.value = currentQuantity;
        }

        // Update value in selectedOptions
        this.selectedOptions.quantity = currentQuantity;
    }

    // Update addToCart function to use quantity from selector
    async addToCart() {
        // Re-entry guard: the add request can take a while and repeated clicks
        // were adding the configuration to the cart multiple times.
        if (this.isAddingToCart) return;
        this.isAddingToCart = true;
        const cartBtn = this.configurator.querySelector('[data-popup-configurator-cart]');
        if (cartBtn) {
            cartBtn.classList.add('is-adding');
            cartBtn.disabled = true;
        }
        try {
            let cartItems = [];

            // Get quantity from quantity selector if available
            let mainProductQuantity = 1;
            const quantitySelector = this.configurator.querySelector('.product-configurator__quantity');
            if (quantitySelector) {
                const quantityInput = quantitySelector.querySelector('.quantity-input');
                if (quantityInput) {
                    mainProductQuantity = parseInt(quantityInput.value) || 1;
                }
            }

            console.log('addToCart called with selectedOptions:', this.selectedOptions);

            if (this.isVariantsAsSeparateProductsMode()) {
                // For separate products mode, we need to add each selected size as a separate cart item
                console.log('Processing separate products mode');

                // Use the buildAllSelectedSizesCartItems method which handles separate products correctly
                cartItems = this.buildAllSelectedSizesCartItems(mainProductQuantity);

                if (cartItems.length === 0) {
                    console.error('No cart items created for separate products');
                    this.showErrorInConfigurator('Unable to create cart items for selected configuration');
                    return;
                }

                console.log('Created cart items for separate products:', cartItems);
            } else {
                // For regular products with combined variants (e.g., "M2 + M3")
                console.log('Processing combined variant product');

                // Check if this is a single-step size product
                const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
                const totalSteps = allSteps.length;
                const sizesStep = this.configurator.querySelector('[data-sizes-step="true"]');

                console.log(`Total steps: ${totalSteps}, has sizes step: ${!!sizesStep}`);

                if (sizesStep) {
                    // Single step size product - collect data from DOM
                    const selectedColor = this.configurator.querySelector('.product-configurator__colors--item.active');
                    const selectedSize = this.configurator.querySelector('.product-configurator__sizes--item.active');

                    console.log('Selected color element:', selectedColor);
                    console.log('Selected size element:', selectedSize);

                    if (!selectedColor || !selectedSize) {
                        console.error('Missing color or size selection for single step product');
                        this.showErrorInConfigurator('Please select color and size');
                        return;
                    }

                    const colorValue = selectedColor.getAttribute('data-color');
                    const materialValue = selectedColor.getAttribute('data-material');
                    const sizeValue = selectedSize.getAttribute('data-size');

                    console.log(`Color: ${colorValue}, Material: ${materialValue}, Size: ${sizeValue}`);
                    console.log('selectedOptions.size:', this.selectedOptions.size);
                    console.log('selectedOptions.secondSize:', this.selectedOptions.secondSize);

                    // For single-step size products, always use combined variant search if we have both sizes
                    let variantId;
                    if (this.selectedOptions.size && this.selectedOptions.secondSize) {
                        console.log('Using combined variant search for single-step product');
                        variantId = this.findCombinedSizeVariantId(colorValue, materialValue, this.selectedOptions.size.value, this.selectedOptions.secondSize.value);
                    } else {
                        console.log('Using single size search for single-step product');
                        variantId = this.findSingleSizeVariantId(colorValue, materialValue, sizeValue);
                    }

                    if (variantId) {
                        const productProperties = this.buildProductProperties();
                        console.log('Single size product properties:', productProperties);
                        cartItems.push({
                            id: variantId,
                            quantity: mainProductQuantity,
                            properties: productProperties
                        });
                        console.log(`Added single size variant ${sizeValue} with variant ${variantId}`);
                    } else {
                        console.error('No matching single size variant found');
                        this.showErrorInConfigurator('Selected size is not available for selected color and material');
                        return;
                    }
                } else if (totalSteps === 4) {
                    // Two step size product - need both sizes
                    if (this.selectedOptions.size && this.selectedOptions.secondSize) {
                        const variantId = this.findCombinedVariantId();
                        if (variantId) {
                            const productProperties = this.buildProductProperties();
                            console.log('Combined variant product properties:', productProperties);
                            cartItems.push({
                                id: variantId,
                                quantity: mainProductQuantity,
                                properties: productProperties
                            });
                            console.log(`Added combined variant ${this.selectedOptions.size.value} + ${this.selectedOptions.secondSize.value} with variant ${variantId}`);
                        } else {
                            console.error('No matching combined variant found');
                            this.showErrorInConfigurator('Selected combination of sizes is not available for selected color and material');
                            return;
                        }
                    } else {
                        console.error('Missing size selections for combined variant product');
                        this.showErrorInConfigurator('Please select both sizes');
                        return;
                    }
                } else {
                    // NEW: Handle products with only color selection (no sizes)
                    console.log('Processing color-only product');

                    const selectedColor = this.configurator.querySelector('.product-configurator__colors--item.active');

                    if (!selectedColor) {
                        console.error('Missing color selection for color-only product');
                        this.showErrorInConfigurator('Please select a color');
                        return;
                    }

                    const variantId = selectedColor.getAttribute('data-variant-id');
                    const colorValue = selectedColor.getAttribute('data-color');
                    const materialValue = selectedColor.getAttribute('data-material');

                    console.log(`Color: ${colorValue}, Material: ${materialValue}, Variant ID: ${variantId}`);

                    if (variantId) {
                        const productProperties = this.buildProductProperties();
                        console.log('Color-only product properties:', productProperties);
                        cartItems.push({
                            id: variantId,
                            quantity: mainProductQuantity,
                            properties: productProperties
                        });
                        console.log(`Added color-only variant ${colorValue} with variant ${variantId}`);
                    } else {
                        console.error('No variant ID found for selected color');
                        this.showErrorInConfigurator('Selected color is not available');
                        return;
                    }
                }
            }

            if (typeof this.addSizePropertiesToForm === 'function') {
                this.addSizePropertiesToForm();
            }

            console.log('Selected options:', this.selectedOptions);
            console.log('Final cart items:', cartItems);

            if (cartItems.length === 0) {
                console.error('No items to add to cart');
                this.showErrorInConfigurator();
                return;
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    items: cartItems
                })
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Cart add response:', result);
                this.closePopup();
                this.updateCartAndOpenDrawer();
            } else {
                const errorText = await response.text();
                console.error('Cart add failed:', response.status, errorText);
                this.showErrorInConfigurator();
            }

        } catch (error) {
            console.error('Error adding to cart:', error);
            this.showErrorInConfigurator();
        } finally {
            this.isAddingToCart = false;
            if (cartBtn) {
                cartBtn.classList.remove('is-adding');
                cartBtn.disabled = false;
            }
        }
    }

    // Updated function to build product properties including orientation
    buildProductProperties(variantId = null) {
        const properties = {};

        // Add orientation if selected
        if (this.selectedOptions.orientation && this.selectedOptions.orientation.title) {
            properties['Oriëntation'] = this.selectedOptions.orientation.title;
            console.log(`Adding orientation property: ${this.selectedOptions.orientation.title}`);
        }

        // For separate products mode, we need to collect all selected sizes from DOM
        if (this.isVariantsAsSeparateProductsMode()) {
            // Get all steps and collect selected sizes
            const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');

            for (let stepNumber = 2; stepNumber <= allSteps.length; stepNumber++) {
                const stepElement = allSteps[stepNumber - 1];

                // Skip if this is not a sizes step
                if (!stepElement.hasAttribute('data-sizes-step') && !stepElement.hasAttribute('data-step-title')) {
                    continue;
                }

                // Find selected size in this step
                const selectedSizeElement = stepElement.querySelector('.product-configurator__sizes--item.active');

                if (selectedSizeElement) {
                    const sizeValue = selectedSizeElement.getAttribute('data-size');
                    const sizeCm = selectedSizeElement.getAttribute('data-size-cm');

                    // Get step title
                    const stepTitle = this.getStepTitleForSize(stepNumber);

                    // Build size description
                    let sizeDescription;
                    if (sizeCm) {
                        sizeDescription = `${sizeCm} (${sizeValue})`;
                    } else {
                        sizeDescription = sizeValue;
                    }

                    properties[stepTitle] = sizeDescription;
                    console.log(`Adding size property for step ${stepNumber}: ${stepTitle}: ${sizeDescription}`);
                }
            }
        } else {
            // Original logic for combined variants
            if (this.selectedOptions.size && this.selectedOptions.size.value) {
                let sizeDescription;
                if (this.selectedOptions.size.cm) {
                    sizeDescription = `${this.selectedOptions.size.cm} (${this.selectedOptions.size.value})`;
                } else {
                    // Try to find cm data from DOM
                    const sizeWithCm = this.findSizeWithCmFromDOM(this.selectedOptions.size.value);
                    sizeDescription = sizeWithCm || this.selectedOptions.size.value;
                }

                const stepTitle = this.getStepTitleForSize(3);
                properties[stepTitle] = sizeDescription;
                console.log(`Adding size property: ${stepTitle}: ${sizeDescription}`);
            }

            if (this.selectedOptions.secondSize && this.selectedOptions.secondSize.value) {
                let secondSizeDescription;
                if (this.selectedOptions.secondSize.cm) {
                    secondSizeDescription = `${this.selectedOptions.secondSize.cm} (${this.selectedOptions.secondSize.value})`;
                } else {
                    // Try to find cm data from DOM
                    const sizeWithCm = this.findSizeWithCmFromDOM(this.selectedOptions.secondSize.value);
                    secondSizeDescription = sizeWithCm || this.selectedOptions.secondSize.value;
                }

                const stepTitle = this.getStepTitleForSize(4);
                properties[stepTitle] = secondSizeDescription;
                console.log(`Adding second size property: ${stepTitle}: ${secondSizeDescription}`);
            }
        }

        // Add other properties if needed
        if (this.selectedOptions.color) {
            properties['_Couleur'] = this.selectedOptions.color;
        }

        if (this.selectedOptions.material) {
            properties['_Matériau'] = this.selectedOptions.material;
        }

        console.log('Built properties object:', properties);
        return properties;
    }

    // New function to find size data from metaobjects
    findSizeDataFromMetaobjects(sizeValue) {
        // This would need to be implemented to search through metaobjects
        // For now, return null to use fallback
        return null;
    }

    // New function to find size with cm data from DOM elements
    findSizeWithCmFromDOM(sizeValue) {
        // Look for size elements in the configurator
        const sizeElements = this.configurator.querySelectorAll('.product-configurator__sizes--item');

        for (let element of sizeElements) {
            const elementSize = element.getAttribute('data-size');
            const elementCm = element.getAttribute('data-size-cm');

            if (elementSize === sizeValue && elementCm) {
                return `${elementCm} (${sizeValue})`;
            }
        }

        return null;
    }

    async updateCartAndOpenDrawer() {
        try {
            const cartDataResponse = await fetch('/cart.js');
            if (!cartDataResponse.ok) {
                throw new Error('Failed to fetch cart data');
            }
            const cartData = await cartDataResponse.json();

            const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart__count, cart-count');
            cartCountElements.forEach(element => {
                element.textContent = cartData.item_count;
            });

            const cartTotalElements = document.querySelectorAll('[data-cart-total], .cart-total, .cart__total');
            cartTotalElements.forEach(element => {
                element.textContent = new Intl.NumberFormat('nl-NL', {
                    style: 'currency',
                    currency: 'EUR'
                }).format(cartData.total_price / 100);
            });

            const cartItemsElement = document.querySelector('cart-items');
            if (cartItemsElement && typeof cartItemsElement.getCart === 'function') {
                cartItemsElement.getCart();
            } else {
                const cartItemsResponse = await fetch('/cart?section_id=api-cart-items');
                if (cartItemsResponse.ok) {
                    const cartHtml = await cartItemsResponse.text();

                    const parser = new DOMParser();
                    const cartDoc = parser.parseFromString(cartHtml, 'text/html');
                    const newCartItems = cartDoc.querySelector('cart-items');

                    if (newCartItems) {
                        const currentCartItems = document.querySelector('cart-items');
                        if (currentCartItems) {
                            currentCartItems.innerHTML = newCartItems.innerHTML;
                        }
                    }
                }
            }

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
                cartDrawer.classList.remove('is-empty');
            }

            document.dispatchEvent(
                new CustomEvent("theme:cart:change", {
                    detail: {
                        cart: cartData,
                        // The theme's <cart-count> element reads detail.cartCount; without it
                        // the header badge briefly rendered the string "undefined".
                        cartCount: cartData.item_count
                    }
                })
            );

            setTimeout(() => {
                if (cartDrawer) {
                    cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
                } else {
                    document.dispatchEvent(new CustomEvent('theme:cart:toggle', {bubbles: true}));
                }
            }, 200);

        } catch (error) {
            console.error('Error updating cart:', error);
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
                cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
            }
        }
    }

    findCorrectVariantId() {
        if (!this.selectedOptions.color || !this.selectedOptions.material) {
            return null;
        }

        const exactMatch = this.findExactVariantMatch();

        if (exactMatch) {
            return exactMatch;
        }

        return null;
    }

    filterUpsellsBySelectedOptions() {
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;

        const upsellItems = this.configurator.querySelectorAll('.product-configurator__upsells--item');

        let visibleCount = 0;

        upsellItems.forEach(item => {
            const variantsData = item.querySelector('.upsell-variants-data');
            if (!variantsData) {
                return;
            }

            const variants = variantsData.querySelectorAll('.variant-data');

            let bestVariant = null;
            let bestMatch = false;

            variants.forEach(variant => {
                const variantMaterial = variant.dataset.material;
                const variantColor = variant.dataset.color;

                if (selectedMaterial && selectedColor &&
                    variantMaterial === selectedMaterial && variantColor === selectedColor) {
                    bestVariant = variant;
                    bestMatch = true;
                }
            });

            if (!bestVariant && variants.length > 0) {
                bestVariant = variants[0];
                bestMatch = false;
            }

            if (bestVariant) {
                const img = item.querySelector('.product-configurator__upsells--img img');
                const price = item.querySelector('.product-configurator__upsells--price');
                const button = item.querySelector('.product-configurator__upsells--btn');

                if (img) img.src = bestVariant.dataset.image;
                if (price) price.textContent = bestVariant.dataset.price;
                if (button) {
                    button.dataset.productId = bestVariant.dataset.variantId;
                    button.dataset.productPrice = bestVariant.dataset.price;
                }

                item.style.display = 'flex';
                visibleCount++;
            } else {
                item.style.display = 'none';
            }
        });

        // Show upsells section only if there are visible items
        const upsellsSection = this.configurator.querySelector('.product-configurator__upsells');
        if (upsellsSection) {
            if (visibleCount > 0) {
                upsellsSection.style.display = 'flex';
            } else {
                upsellsSection.style.display = 'none';
            }
        }
    }

    showFirstVariantOfEachUpsell() {
        const upsellItems = this.configurator.querySelectorAll('.product-configurator__upsells--item');

        const upsellGroups = {};
        upsellItems.forEach(item => {
            const productId = item.dataset.upsellProductId;
            if (!upsellGroups[productId]) {
                upsellGroups[productId] = [];
            }
            upsellGroups[productId].push(item);
        });

        let visibleCount = 0;

        Object.keys(upsellGroups).forEach(productId => {
            const productVariants = upsellGroups[productId];
            if (productVariants.length > 0) {
                const firstVariant = productVariants[0];
                firstVariant.style.display = 'block';
                visibleCount++;

                productVariants.slice(1).forEach(variant => {
                    variant.style.display = 'none';
                });
            }
        });

        const upsellsSection = this.configurator.querySelector('.product-configurator__upsells');
        if (upsellsSection) {
            if (visibleCount > 0) {
                upsellsSection.style.display = 'block';
            } else {
                upsellsSection.style.display = 'none';
            }
        }
    }

    hideAllUpsells() {
        const upsellItems = this.configurator.querySelectorAll('.product-configurator__upsells--item');
        upsellItems.forEach(item => {
            item.style.display = 'none';
        });

        const upsellsSection = this.configurator.querySelector('.product-configurator__upsells');
        if (upsellsSection) {
            upsellsSection.style.display = 'none';
        }
    }

    autoSelectFirstColor() {
        const step2 = this.configurator.querySelector('[data-step="1"]');
        if (!step2) {
            return;
        }

        const firstColorItem = step2.querySelector('.product-configurator__colors--item:not(.sold-out)');
        if (firstColorItem) {
            console.log('Auto-selecting first color:', firstColorItem);
            this.selectColor(firstColorItem);
        }
    }

    // Update buildSeparateProductsCartItems function to support quantity
    buildSeparateProductsCartItems(quantity = 1) {
        const cartItems = [];

        // Add main product
        if (this.selectedOptions.size && this.selectedOptions.color && this.selectedOptions.material) {
            const mainVariantId = this.findMainProductVariantId();
            if (mainVariantId) {
                cartItems.push({
                    id: mainVariantId,
                    quantity: quantity
                });
            }
        }

        // Add additional products
        if (this.selectedOptions.additionalProduct1 && this.selectedOptions.additionalProduct1VariantId) {
            cartItems.push({
                id: this.selectedOptions.additionalProduct1VariantId,
                quantity: 1
            });
        }

        if (this.selectedOptions.additionalProduct2 && this.selectedOptions.additionalProduct2VariantId) {
            cartItems.push({
                id: this.selectedOptions.additionalProduct2VariantId,
                quantity: 1
            });
        }

        if (this.selectedOptions.additionalProduct3 && this.selectedOptions.additionalProduct3VariantId) {
            cartItems.push({
                id: this.selectedOptions.additionalProduct3VariantId,
                quantity: 1
            });
        }

        return cartItems;
    }

    // New function to collect all selected sizes
    buildAllSelectedSizesCartItems(quantity = 1) {
        const cartItems = [];

        // Add sizes from all steps (including the main product step)
        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        const totalSteps = allSteps.length;

        console.log(`Total steps found: ${totalSteps}`);

        // Go through all steps and collect selected sizes - START FROM STEP 2 (as sizes start from step 2)
        for (let stepNumber = 2; stepNumber <= totalSteps; stepNumber++) {
            const stepElement = allSteps[stepNumber - 1];

            console.log(`Checking step ${stepNumber}:`, stepElement);

            // Check for different types of size steps
            const hasSizesStep = stepElement.hasAttribute('data-sizes-step');
            const hasSizesSecondaryStep = stepElement.hasAttribute('data-sizes-secondary-step');
            const hasAdditionalProductStep = stepElement.hasAttribute('data-additional-product-step');
            const hasBackAndArmrestsStep = stepElement.hasAttribute('data-back-and-armrests-step');
            const hasStepTitle = stepElement.hasAttribute('data-step-title');
            const hasSizesItems = stepElement.querySelector('.product-configurator__sizes--item') !== null;

            console.log(`Step ${stepNumber} - hasSizesStep: ${hasSizesStep}, hasSizesSecondaryStep: ${hasSizesSecondaryStep}, hasAdditionalProductStep: ${hasAdditionalProductStep}, hasBackAndArmrestsStep: ${hasBackAndArmrestsStep}, hasStepTitle: ${hasStepTitle}, hasSizesItems: ${hasSizesItems}`);

            // Skip if this is not a sizes step - check for any size-related attribute
            const isSizeStep = hasSizesStep || hasSizesSecondaryStep || hasAdditionalProductStep || hasBackAndArmrestsStep || (hasStepTitle && hasSizesItems);

            if (!isSizeStep) {
                console.log(`Skipping step ${stepNumber} - not a sizes step`);
                continue;
            }

            // Find selected size in this step
            const selectedSizeElement = stepElement.querySelector('.product-configurator__sizes--item.active');

            if (selectedSizeElement) {
                const sizeValue = selectedSizeElement.getAttribute('data-size');
                const sizeCm = selectedSizeElement.getAttribute('data-size-cm');

                console.log(`Found selected size in step ${stepNumber}: ${sizeValue} (${sizeCm})`);

                // For separate products mode, we need to find the correct variant
                // Check if this is a combined size product (like M3 + M4)
                let variantId;

                if (this.isCombinedSizeProduct()) {
                    // For combined size products, we need to find the variant that contains both sizes
                    variantId = this.findCombinedSizeVariantForSeparateProducts(sizeValue);
                } else {
                    // For regular separate products, find individual variants
                    variantId = this.findSingleSizeVariantId(this.selectedOptions.color, this.selectedOptions.material, sizeValue);
                }

                if (variantId) {
                    // Build properties for this specific product
                    const stepTitle = this.getStepTitleForSize(stepNumber);
                    const productProperties = this.buildPropertiesForSingleProduct(sizeValue, sizeCm, stepTitle);

                    cartItems.push({
                        id: variantId,
                        quantity: 1,
                        properties: productProperties
                    });

                    console.log(`Added separate product for size ${sizeValue} with variant ${variantId} and properties:`, productProperties);
                } else {
                    console.log(`No variant found for size ${sizeValue}`);
                }
            } else {
                console.log(`No selected size found in step ${stepNumber}`);
            }
        }

        console.log(`Total cart items created: ${cartItems.length}`);
        return cartItems;
    }

    // New function to check if this is a combined size product
    isCombinedSizeProduct() {
        // Check if we have multiple size steps and they represent a combination
        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');
        let sizeStepsCount = 0;

        for (let step of allSteps) {
            const hasSizesStep = step.hasAttribute('data-sizes-step');
            const hasSizesSecondaryStep = step.hasAttribute('data-sizes-secondary-step');
            const hasAdditionalProductStep = step.hasAttribute('data-additional-product-step');
            const hasBackAndArmrestsStep = step.hasAttribute('data-back-and-armrests-step');
            const hasStepTitle = step.hasAttribute('data-step-title');
            const hasSizesItems = step.querySelector('.product-configurator__sizes--item') !== null;

            const isSizeStep = hasSizesStep || hasSizesSecondaryStep || hasAdditionalProductStep || hasBackAndArmrestsStep || (hasStepTitle && hasSizesItems);

            if (isSizeStep) {
                sizeStepsCount++;
            }
        }

        // If we have exactly 2 size steps, it's likely a combined size product
        return sizeStepsCount === 2;
    }

    // New function to find combined size variant for separate products mode
    findCombinedSizeVariantForSeparateProducts(sizeValue) {
        if (!this.selectedOptions.color || !this.selectedOptions.material) {
            console.log('Missing color or material for combined size variant');
            return null;
        }

        const variants = window.productVariants || [];
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;

        console.log(`Looking for combined size variant: ${selectedMaterial} / ${selectedColor} / ${sizeValue}`);

        // Get all selected sizes from all steps
        const allSelectedSizes = this.getAllSelectedSizes();
        console.log('All selected sizes:', allSelectedSizes);

        for (let variant of variants) {
            const title = variant.title.toLowerCase();
            const materialMatch = title.includes(selectedMaterial.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = selectedColor.replace(/-/g, ' ');
            const colorWithDashes = selectedColor.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            // Check if this variant contains the current size
            const sizeMatch = title.includes(sizeValue.toLowerCase());

            console.log(`Variant ${variant.id}: "${variant.title}" - materialMatch: ${materialMatch}, colorMatch: ${colorMatch}, sizeMatch: ${sizeMatch}`);

            if (materialMatch && colorMatch && sizeMatch) {
                console.log(`Found matching combined size variant: ${variant.id}`);
                return variant.id;
            }
        }

        console.log('No matching combined size variant found');
        return null;
    }

    // New function to get all selected sizes from all steps
    getAllSelectedSizes() {
        const selectedSizes = [];
        const allSteps = this.configurator.querySelectorAll('.product-configurator__wrapper');

        for (let step of allSteps) {
            const hasSizesStep = step.hasAttribute('data-sizes-step');
            const hasSizesSecondaryStep = step.hasAttribute('data-sizes-secondary-step');
            const hasAdditionalProductStep = step.hasAttribute('data-additional-product-step');
            const hasBackAndArmrestsStep = step.hasAttribute('data-back-and-armrests-step');
            const hasStepTitle = step.hasAttribute('data-step-title');
            const hasSizesItems = step.querySelector('.product-configurator__sizes--item') !== null;

            const isSizeStep = hasSizesStep || hasSizesSecondaryStep || hasAdditionalProductStep || hasBackAndArmrestsStep || (hasStepTitle && hasSizesItems);

            if (isSizeStep) {
                const selectedSizeElement = step.querySelector('.product-configurator__sizes--item.active');
                if (selectedSizeElement) {
                    const sizeValue = selectedSizeElement.getAttribute('data-size');
                    selectedSizes.push(sizeValue);
                }
            }
        }

        return selectedSizes;
    }

    // New function to build properties for a single product
    buildPropertiesForSingleProduct(sizeValue, sizeCm, stepTitle) {
        const properties = {};

        // Add orientation if selected
        if (this.selectedOptions.orientation && this.selectedOptions.orientation.title) {
            properties['Oriëntation'] = this.selectedOptions.orientation.title;
        }

        // Add size property for this specific product
        let sizeDescription;
        if (sizeCm) {
            sizeDescription = `${sizeCm} (${sizeValue})`;
        } else {
            sizeDescription = sizeValue;
        }

        properties[stepTitle] = sizeDescription;

        // Add color and material properties
        if (this.selectedOptions.color) {
            properties['_Couleur'] = this.selectedOptions.color;
        }

        if (this.selectedOptions.material) {
            properties['_Matériau'] = this.selectedOptions.material;
        }

        console.log(`Built properties for ${stepTitle}:`, properties);
        return properties;
    }

    // Function to find variant ID by size - FIXED for separate products
    findVariantIdForSize(sizeValue) {
        if (!sizeValue || !this.selectedOptions.color || !this.selectedOptions.material) {
            return null;
        }

        // For separate products mode, we need to find individual variants for each size
        // NOT combinations
        return this.findSingleSizeVariantId(this.selectedOptions.color, this.selectedOptions.material, sizeValue);
    }

    findMainProductVariantId() {
        if (!this.selectedOptions.size || !this.selectedOptions.color || !this.selectedOptions.material) {
            return null;
        }

        const variants = window.productVariants || [];
        const selectedMaterial = this.selectedOptions.material;
        const selectedColor = this.selectedOptions.color;
        const selectedSize = this.selectedOptions.size.value;

        for (let variant of variants) {
            const title = variant.title.toLowerCase();
            const sizeMatch = title.includes(selectedSize.toLowerCase());
            const materialMatch = title.includes(selectedMaterial.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = selectedColor.replace(/-/g, ' ');
            const colorWithDashes = selectedColor.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            if (sizeMatch && materialMatch && colorMatch) {
                return variant.id;
            }
        }

        return null;
    }

    findCombinedVariantId() {
        if (!this.selectedOptions.size || !this.selectedOptions.secondSize || !this.selectedOptions.color || !this.selectedOptions.material) {
            console.log('Missing required options for combined variant');
            return null;
        }

        const variants = window.productVariants || [];
        const size1 = this.selectedOptions.size.value;
        const size2 = this.selectedOptions.secondSize.value;
        const material = this.selectedOptions.material;
        const color = this.selectedOptions.color;

        console.log(`Looking for combined variant: ${size1} + ${size2}, material: ${material}, color: ${color}`);

        for (let variant of variants) {
            const title = variant.title.toLowerCase();
            const sizeMatch = title.includes(`${size1.toLowerCase()} + ${size2.toLowerCase()}`) ||
                title.includes(`${size2.toLowerCase()} + ${size1.toLowerCase()}`);
            const materialMatch = title.includes(material.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = color.replace(/-/g, ' ');
            const colorWithDashes = color.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            console.log(`Variant ${variant.id}: "${variant.title}" - sizeMatch: ${sizeMatch}, materialMatch: ${materialMatch}, colorMatch: ${colorMatch}`);

            if (sizeMatch && materialMatch && colorMatch) {
                console.log(`Found matching combined variant: ${variant.id}`);
                return variant.id;
            }
        }

        console.log('No matching combined variant found');
        return null;
    }

    checkColorAvailability() {
        const colorsStep = this.configurator.querySelector('[data-colors-step="true"]');
        if (!colorsStep) return;

        const colorItems = colorsStep.querySelectorAll('.product-configurator__colors--item');
        const availableColors = colorsStep.querySelectorAll('.product-configurator__colors--item:not(.sold-out)');
        const nextButton = colorsStep.querySelector('.product-configurator__next');

        if (nextButton) {
            if (availableColors.length === 0) {
                nextButton.disabled = true;
                nextButton.classList.add('disabled');
                nextButton.style.opacity = '0.5';
                nextButton.style.cursor = 'not-allowed';
            } else {
                nextButton.disabled = false;
                nextButton.classList.remove('disabled');
                nextButton.style.opacity = '1';
                nextButton.style.cursor = 'pointer';
            }
        }
    }

    // New function to find single size variant
    findSingleSizeVariantId(color, material, size) {
        if (!color || !material || !size) {
            console.log('Missing required parameters for single size variant');
            return null;
        }

        const variants = window.productVariants || [];

        console.log(`Looking for single size variant: ${material} / ${color} / ${size}`);

        for (let variant of variants) {
            const title = variant.title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            const materialMatch = title.includes(material.toLowerCase());

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = color.replace(/-/g, ' ');
            const colorWithDashes = color.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            // For this specific case, we need to find a variant that contains both M3 and M4
            // but we're looking for the variant that represents the combination
            let sizeMatch = false;

            // Check if this is a two-size product (M3 + M4 combination)
            if (size === 'M4' && this.selectedOptions.secondSize && this.selectedOptions.secondSize.value === 'M3') {
                // Look for variant that contains both M3 and M4
                sizeMatch = title.includes('m3') && title.includes('m4');
            } else {
                // For single size products, use the original logic
                sizeMatch = title.includes(size.toLowerCase());
            }

            console.log(`Variant ${variant.id}: "${variant.title}" - materialMatch: ${materialMatch}, colorMatch: ${colorMatch}, sizeMatch: ${sizeMatch}`);

            if (materialMatch && colorMatch && sizeMatch) {
                console.log(`Found matching single size variant: ${variant.id}`);
                return variant.id;
            }
        }

        console.log('No matching single size variant found');
        return null;
    }

    // New function to find combined size variant (for products with M3 + M4)
    findCombinedSizeVariantId(color, material, size1, size2) {
        if (!color || !material || !size1 || !size2) {
            console.log('Missing required parameters for combined size variant');
            return null;
        }

        const variants = window.productVariants || [];

        console.log(`Looking for combined size variant: ${material} / ${color} / ${size1} + ${size2}`);

        // Normalize material to handle diacritics (e.g., "texture" from "Texturé")
        const normalizedMaterial = material.toLowerCase();

        for (let variant of variants) {
            // Normalize title to remove diacritics for proper matching
            const title = variant.title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            const materialMatch = title.includes(normalizedMaterial);

            // Fix color matching - handle both formats (with dashes and spaces)
            const colorWithSpaces = color.replace(/-/g, ' ');
            const colorWithDashes = color.replace(/ /g, '-');
            const colorMatch = title.includes(colorWithSpaces) || title.includes(colorWithDashes);

            // Look for size combination in both directions
            const size1Lower = size1.toLowerCase();
            const size2Lower = size2.toLowerCase();
            const sizeMatch = (title.includes(`${size1Lower} + ${size2Lower}`) ||
                title.includes(`${size2Lower} + ${size1Lower}`));

            console.log(`Variant ${variant.id}: "${variant.title}" - materialMatch: ${materialMatch}, colorMatch: ${colorMatch}, sizeMatch: ${sizeMatch}`);

            if (materialMatch && colorMatch && sizeMatch) {
                console.log(`Found matching combined size variant: ${variant.id}`);
                return variant.id;
            }
        }

        console.log('No matching combined size variant found');
        return null;
    }

    // New function to get step title for a given step number
    getStepTitleForSize(stepNumber) {
        const stepElement = this.configurator.querySelector(`[data-step="${stepNumber}"]`);
        if (stepElement && stepElement.dataset.stepTitle) {
            return stepElement.dataset.stepTitle;
        }

        // Fallback to default title "Afmeting" if no step title found
        return 'Dimensions';
    }
}

// Material Tooltip Functionality
function initMaterialTooltips() {
    const tooltipButtons = document.querySelectorAll('.material-tooltip-btn');

    tooltipButtons.forEach(button => {
        button.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            const materialHandle = this.getAttribute('data-material');
            const tooltip = document.getElementById(`material-tooltip-${materialHandle}`);

            if (tooltip) {
                // Remove active class from all tooltips and buttons
                document.querySelectorAll('.material-tooltip').forEach(t => {
                    t.classList.remove('active');
                });
                document.querySelectorAll('.material-tooltip-btn').forEach(btn => {
                    btn.classList.remove('active');
                });

                // Add active class to current tooltip and button
                tooltip.classList.add('active');
                this.classList.add('active');
            }
        });
    });

    // Close tooltips when clicking outside
    document.addEventListener('click', function (e) {
        if (!e.target.closest('.material-tooltip') && !e.target.closest('.material-tooltip-btn')) {
            document.querySelectorAll('.material-tooltip').forEach(tooltip => {
                tooltip.classList.remove('active');
                // tooltip.style.display = 'none';
            });
            document.querySelectorAll('.material-tooltip-btn').forEach(btn => {
                btn.classList.remove('active');
            });
        }
    });

    // Close tooltips when clicking close button
    document.querySelectorAll('.material-tooltip-close').forEach(closeBtn => {
        closeBtn.addEventListener('click', function () {
            const tooltip = this.closest('.material-tooltip');
            const button = document.querySelector(`[data-material="${tooltip.id.replace('material-tooltip-', '')}"]`);

            if (tooltip) {
                tooltip.classList.remove('active');
            }
            if (button) {
                button.classList.remove('active');
            }
        });
    });
}

// Initialize tooltips when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
    initMaterialTooltips();
});


document.addEventListener('DOMContentLoaded', () => {
    const configurator = document.querySelector('.product-configurator');

    if (configurator) {
        new ProductConfigurator();
    } else {
        console.warn('Product configurator element not found on page');
    }
});

